/**
 * DiscoverySummaryCard Component
 * Displays 7-day discovery statistics and last run status
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useDiscoverySummary } from '@/hooks/useDiscoverySummary';
import { 
  Inbox, 
  CheckCircle, 
  Copy, 
  XCircle,
  Clock,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface StatBoxProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color?: 'default' | 'green' | 'amber' | 'red';
}

function StatBox({ label, value, icon, color = 'default' }: StatBoxProps) {
  const colorClasses = {
    default: 'text-foreground',
    green: 'text-green-600 dark:text-green-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
  };

  return (
    <div className="flex flex-col items-center p-3 rounded-lg bg-muted/30">
      <div className={`mb-1 ${colorClasses[color]}`}>
        {icon}
      </div>
      <span className={`text-2xl font-bold ${colorClasses[color]}`}>
        {value}
      </span>
      <span className="text-xs text-muted-foreground text-center">
        {label}
      </span>
    </div>
  );
}

export function DiscoverySummaryCard() {
  const { data: summary, isLoading, error } = useDiscoverySummary();

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading summary...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !summary) {
    return null; // Silently fail if summary can't be loaded
  }

  const getTriggerLabel = (trigger: string | null) => {
    switch (trigger) {
      case 'scheduled': return 'Scheduled';
      case 'manual': return 'Manual';
      case 'manager_request': return 'Manager';
      case 'gap_fill': return 'Gap Fill';
      default: return trigger || 'Unknown';
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Discovery Summary — Last 7 Days
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox 
            label="New Pending" 
            value={summary.newPending} 
            icon={<Inbox className="h-5 w-5" />}
          />
          <StatBox 
            label="Promoted" 
            value={summary.promoted} 
            icon={<CheckCircle className="h-5 w-5" />}
            color="green"
          />
          <StatBox 
            label="Duplicates" 
            value={summary.duplicates} 
            icon={<Copy className="h-5 w-5" />}
            color="amber"
          />
          <StatBox 
            label="Rejected" 
            value={summary.rejected} 
            icon={<XCircle className="h-5 w-5" />}
            color="red"
          />
        </div>

        <Separator />

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Last Run:</span>
          {summary.lastRun ? (
            <>
              <Badge 
                variant={summary.lastRun.status === 'success' ? 'default' : 'destructive'}
                className="text-xs"
              >
                {summary.lastRun.status}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {getTriggerLabel(summary.lastRun.triggerReason)}
              </Badge>
              <span className="text-muted-foreground">
                {formatDistanceToNow(new Date(summary.lastRun.timestamp), { addSuffix: true })}
              </span>
              {summary.lastRun.error && (
                <span className="flex items-center gap-1 text-destructive text-xs">
                  <AlertCircle className="h-3 w-3" />
                  {summary.lastRun.error.slice(0, 50)}...
                </span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground italic">Never run</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default DiscoverySummaryCard;
